import React from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { TaxIcon, DocumentsIcon, PencilIcon } from '../ui/Icons';

interface TaxDocument {
  id: string;
  type: 'federal' | 'state' | 'property' | 'other';
  year: string;
  description: string;
  status: 'filed' | 'pending' | 'extension';
  amount?: number;
  documents: Array<{
    name: string;
    url: string;
    uploadDate: string;
  }>;
  notes?: string;
}

interface TaxCategory {
  key: 'federal' | 'state' | 'property' | 'other';
  label: string;
  Icon: React.FC<{ className?: string }>;
}

const Frame240: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSessionPersistence();

  const taxDocs: TaxDocument[] =
    session.taskProgress?.['tax_documents']?.answers?.documents || [];

  const categories: TaxCategory[] = [
    { key: 'federal', label: 'Federal Tax Returns', Icon: TaxIcon },
    { key: 'state', label: 'State Tax Returns', Icon: TaxIcon },
    { key: 'property', label: 'Property Tax Records', Icon: DocumentsIcon },
    { key: 'other', label: 'Other Tax Documents', Icon: DocumentsIcon },
  ];

  const handleEdit = () => navigate('/frame-239');
  const handleBack = () => navigate('/frame-239');
  const handleNext = () => navigate('/frame-213');

  const totalsByStatus = taxDocs.reduce((acc, doc) => {
    acc[doc.status] = (acc[doc.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const totalsByTypeAmount = taxDocs.reduce((acc, doc) => {
    if (typeof doc.amount === 'number') {
      acc[doc.type] = (acc[doc.type] || 0) + doc.amount;
    }
    return acc;
  }, {} as Record<string, number>);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <ResponsiveContainer background="stone">
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2">
        <BrandSymbol size="3xl" showText variant="dark" />
      </div>

      <ResponsiveContent className="pt-32">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-cyan-900 text-3xl font-semibold font-['Test_Domaine_Display'] mb-8">
            Tax Documents Summary
          </h1>

          {/* Status Overview */}
          <div className="bg-white rounded-lg border border-stone-200 shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-stone-800">Document Status</h2>

              <button
                onClick={handleEdit}
                className="text-cyan-600 hover:text-cyan-700 flex items-center gap-2"
              >
                <PencilIcon className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {taxDocs.length > 0 ? 'Edit Documents' : 'Add Documents'}
                </span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="text-green-800 font-medium">Filed</div>
                <div className="text-2xl font-bold text-green-700">
                  {totalsByStatus.filed || 0}
                </div>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                <div className="text-yellow-800 font-medium">Pending</div>
                <div className="text-2xl font-bold text-yellow-700">
                  {totalsByStatus.pending || 0}
                </div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="text-blue-800 font-medium">Extension Filed</div>
                <div className="text-2xl font-bold text-blue-700">
                  {totalsByStatus.extension || 0}
                </div>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-8">
            {categories.map((category) => {
              const categoryDocs = taxDocs.filter((d) => d.type === category.key);
              const totalAmount = totalsByTypeAmount[category.key];

              return (
                <div
                  key={category.key}
                  className="bg-white rounded-lg border border-stone-200 shadow-sm p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <category.Icon className="w-6 h-6 text-cyan-700" />
                      <h2 className="text-xl font-semibold text-stone-800">{category.label}</h2>
                    </div>

                    <button
                      onClick={handleEdit}
                      className="text-cyan-600 hover:text-cyan-700 flex items-center gap-2"
                    >
                      <PencilIcon className="w-5 h-5" />
                      <span className="text-sm font-medium">Edit</span>
                    </button>
                  </div>

                  {typeof totalAmount === 'number' && totalAmount > 0 && (
                    <div className="text-lg font-medium text-cyan-700 mb-4">
                      Total: {formatCurrency(totalAmount)}
                    </div>
                  )}

                  {categoryDocs.length === 0 ? (
                    <div className="text-stone-500">
                      No {category.label.toLowerCase()} added yet.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {categoryDocs.map((doc) => (
                        <div key={doc.id} className="border-t border-stone-100 pt-4">
                          <div className="flex justify-between items-start gap-4">
                            <div className="min-w-0">
                              <h3 className="font-medium text-stone-800 truncate">
                                {doc.description || 'Untitled document'}
                              </h3>
                              <div className="text-sm text-stone-500 mt-1">
                                Year: {doc.year || 'N/A'} • Status:{' '}
                                {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                                {typeof doc.amount === 'number'
                                  ? ` • Amount: ${formatCurrency(doc.amount)}`
                                  : ''}
                              </div>

                              {doc.notes && (
                                <div className="text-sm text-stone-600 mt-2">
                                  <span className="font-medium text-stone-700">Notes:</span> {doc.notes}
                                </div>
                              )}
                            </div>

                            <div className="text-sm text-cyan-700 font-medium whitespace-nowrap">
                              {doc.documents?.length || 0} document(s)
                            </div>
                          </div>

                          {doc.documents?.length > 0 && (
                            <div className="mt-3 space-y-1">
                              <div className="text-sm font-medium text-stone-600">Files</div>
                              {doc.documents.map((file, idx) => (
                                <div
                                  key={`${doc.id}-${idx}`}
                                  className="text-sm text-stone-600 flex items-center gap-2 ml-2"
                                >
                                  <DocumentsIcon className="w-4 h-4" />
                                  <span className="truncate">{file.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Empty overall state helper */}
          {taxDocs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-stone-600 text-lg">No tax documents have been added yet.</p>
              <button
                onClick={handleEdit}
                className="mt-4 px-6 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800"
              >
                Add Tax Documents
              </button>
            </div>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Tax Documents and Records"
        subtitle="Review Summary"
        progressPercent={100}
        onBack={handleBack}
        backText="← Back"
        onNext={handleNext}
        nextText="Complete →"
      />
    </ResponsiveContainer>
  );
};

export default Frame240;